import type { OrderInput } from "../../worker/schemas/order";

export interface AppConfig {
  turnstileSiteKey: string | null;
  displayPriceCents: number;
  supportEmail: string;
}

export interface CheckoutResponse {
  checkoutUrl: string;
  publicOrderToken: string;
}

export interface OrderStatusResponse {
  status:
    | "pending"
    | "paid"
    | "queued"
    | "generating"
    | "preparing_package"
    | "complete"
    | "failed";
  artistName: string;
  songTitle: string;
  createdAt: string;
  completedAt: string | null;
  downloadReady: boolean;
  errorMessage: string | null;
  downloadToken: string | null;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly code: string,
    public readonly issues?: Array<{ path: string; message: string }>,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
  });

  if (!res.ok) {
    const body = await res.json().catch(() => null);
    const error = (
      body as { error?: { code: string; message: string; issues?: unknown[] } }
    )?.error;
    throw new ApiError(
      error?.message ?? "Something went wrong. Please try again.",
      error?.code ?? "unknown_error",
      error?.issues as Array<{ path: string; message: string }> | undefined,
    );
  }

  return res.json() as Promise<T>;
}

export function getConfig(): Promise<AppConfig> {
  return request<AppConfig>("/api/config");
}

export function submitCampaignOrder(input: OrderInput): Promise<CheckoutResponse> {
  return request<CheckoutResponse>("/api/checkout", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function getOrderStatus(token: string): Promise<OrderStatusResponse> {
  return request<OrderStatusResponse>(`/api/orders/${encodeURIComponent(token)}/status`);
}
